import React from 'react';
import { cn } from '../lib/utils';

/**
 * The mark from the Redbax logo, on its own.
 *
 * The geometry is LIFTED FROM favicon.svg rather than redrawn: the same hexagonal shield, the same eight
 * legs at the same angles, the same inner hex and centre dot. Drawing a second spider by eye would give
 * the product two subtly different spiders, and the one on the sign-up screen is the first one anybody
 * sees.
 *
 * Colours come from the theme tokens as CSS variables, so the body tracks the palette. The legs and the
 * shield outline use `currentColor`, which lets a caller tint a whole spider by setting a text colour -
 * that is what makes the cluster below able to fade its outer spiders into the background.
 */
export interface SpiderProps extends React.SVGProps<SVGSVGElement> {
  /** Rendered width and height in pixels. */
  size?: number;
  /** Adds the idle leg-drift animation. Off by default so a page can choose where motion belongs. */
  animate?: boolean;
}

const LEGS_LEFT = [
  'M9 11L3 7',
  'M8 14L2 14',
  'M8 18L2 18',
  'M9 21L3 25',
];

const LEGS_RIGHT = [
  'M23 11L29 7',
  'M24 14L30 14',
  'M24 18L30 18',
  'M23 21L29 25',
];

export function Spider({ size = 48, animate = false, className, ...rest }: SpiderProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
      className={cn('shrink-0', animate && 'animate-spider-drift', className)}
      {...rest}
    >
      {/* Legs first, so the body sits over the point where they meet it. Each side is staggered on
          hover, which reads as a scuttle rather than a single rigid twitch. */}
      <g stroke="currentColor" strokeWidth="1.2" strokeLinecap="round">
        {LEGS_LEFT.map((d, i) => (
          <path
            key={d}
            d={d}
            className="origin-[16px_16px] transition-transform duration-300 ease-out group-hover:-rotate-3"
            style={{ transitionDelay: `${i * 40}ms` }}
          />
        ))}
        {LEGS_RIGHT.map((d, i) => (
          <path
            key={d}
            d={d}
            className="origin-[16px_16px] transition-transform duration-300 ease-out group-hover:rotate-3"
            style={{ transitionDelay: `${i * 40}ms` }}
          />
        ))}
      </g>

      {/* Hexagonal shield — the body. */}
      <path
        d="M16 5L25 10V22L16 27L7 22V10L16 5Z"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="var(--color-background)"
      />
      <path d="M16 10L21 13V19L16 22L11 19V13L16 10Z" fill="var(--color-red-hover)" />
      <circle cx="16" cy="16" r="2" fill="var(--color-red-accent)" />
    </svg>
  );
}

/**
 * One spider on a dropline, for the individual account.
 *
 * The thread is what makes a lone spider read as deliberate rather than as a spider that lost the others.
 * It descends on hover, which is the whole animation: no keyframes, just a transform the browser can
 * interpolate, and it costs nothing when reduced motion is on because the height transition is the only
 * thing moving.
 */
export function SoloSpider({ size = 76 }: { size?: number }) {
  return (
    // Anchored to the top of its container rather than centred, so the thread runs from the card's edge
    // and the spider reads as hanging into the space. Centred, it looked like a spider that happened to
    // have a short line above it.
    <div className="flex h-full flex-col items-center justify-start" aria-hidden="true">
      <div className="w-px flex-1 bg-gradient-to-b from-transparent to-border" />
      <div className="h-5 w-px bg-border transition-[height] duration-500 ease-out group-hover:h-10" />
      <Spider size={size} className="text-text-primary" />
      <div className="flex-1" />
    </div>
  );
}

/**
 * A group of spiders for the company account.
 *
 * FIVE, arranged asymmetrically. A symmetrical ring reads as a pattern; an off-centre group reads as a
 * team. The outer four are smaller and dimmer so the eye still lands on one of them first, and each is
 * nudged outward on hover with its own delay, so the group looks like it disperses slightly rather than
 * scaling as one block.
 *
 * Sized in a fixed box rather than by content, so both cards' artwork occupies the same height and the
 * two choices stay visually level — a taller card reads as the recommended one, and that is not the
 * message here.
 */
export function SpiderGroup({ size = 132 }: { size?: number }) {
  // x / y as percentages of the box, scale relative to the box, and the hover nudge. Pushed out to the
  // corners rather than clustered tight: at close spacing the legs of neighbouring spiders interleave
  // and the group reads as one tangled shape instead of five creatures.
  //
  // Every position keeps `left + scale` and `top + scale` inside the box, so the group cannot spill out
  // of the space reserved for it. It did: the bottom-right spider at 64% with a 0.46 scale reached 110%
  // and crowded the heading underneath, which made the two cards look misaligned.
  const others = [
    { left: '0%', top: '2%', scale: 0.42, dx: -6, dy: -4, delay: 0 },
    { left: '62%', top: '0%', scale: 0.36, dx: 6, dy: -5, delay: 60 },
    { left: '-2%', top: '56%', scale: 0.34, dx: -7, dy: 4, delay: 120 },
    { left: '56%', top: '52%', scale: 0.46, dx: 7, dy: 5, delay: 180 },
  ];

  return (
    <div className="relative" style={{ width: size, height: size }} aria-hidden="true">
      {others.map((s) => (
        <span
          key={`${s.left}-${s.top}`}
          className="absolute text-text-secondary/70 transition-transform duration-500 ease-out"
          style={{
            left: s.left,
            top: s.top,
            // The nudge is applied through a CSS variable the group-hover rule overrides, so each
            // spider keeps its own offset instead of every one sharing a single transform.
            transform: 'translate(var(--sx, 0px), var(--sy, 0px))',
            transitionDelay: `${s.delay}ms`,
            ['--hx' as string]: `${s.dx}px`,
            ['--hy' as string]: `${s.dy}px`,
          }}
          data-spider-nudge
        >
          <Spider size={size * s.scale} />
        </span>
      ))}

      {/* The lead spider, slightly off-centre and full brightness. */}
      <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-text-primary">
        <Spider size={size * 0.54} />
      </span>
    </div>
  );
}
